// src/services/workflow.ts

import api from './api';

export interface WorkflowPendingResponse {
  data: any[];
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface WorkflowValidationRequest {
  feedback?: string;
  approval_note_html?: string;
  resolution_note_html?: string;
  resolution_evidence_url?: string;
}

export interface WorkflowSummaryStep {
  step_order: number;
  step_label: string | null;
  can_resolve_here: boolean;
  validator: { id: number; name: string } | null;
  validator_role: string | null;
  status: string;
  feedback: string | null;
  metadata?: Record<string, any> | null;
  action_at: string | null;
}

export interface WorkflowSummaryResponse {
  workflow_config: any;
  current_step: number | null;
  total_steps: number;
  completed: number;
  can_approve: boolean;
  can_reject: boolean;
  can_resolve: boolean;
  resolution: string | null;
  needs_rapim: boolean | null;
  steps: WorkflowSummaryStep[];
}

export const workflowService = {
  getPending: async (params?: { per_page?: number; page?: number }) => {
    const res = await api.get('/workflow/pending', { params });
    return res.data as WorkflowPendingResponse;
  },

  getStats: async () => {
    const res = await api.get('/workflow/stats');
    return (res.data?.data || res.data) as {
      pending: number;
      approved_today: number;
      rejected_today: number;
      resolved_today: number;
    };
  },

  getHistory: async (params?: { per_page?: number; page?: number }) => {
    const res = await api.get('/workflow/history', { params });
    return res.data as WorkflowPendingResponse;
  },

  getSummary: async (laporanId: number) => {
    const res = await api.get(`/laporan/${laporanId}/workflow-summary`);
    return (res.data?.data || res.data) as WorkflowSummaryResponse;
  },

  processValidation: async (
    laporanId: number,
    action: 'approve' | 'reject' | 'resolve',
    payload?: string | WorkflowValidationRequest
  ) => {
    const body = typeof payload === 'string' || payload == null
      ? { action, feedback: payload }
      : { action, ...payload };

    const res = await api.post(`/laporan/${laporanId}/workflow-validate`, {
      ...body,
    });
    return res.data;
  },

  getConfigs: async () => {
    const res = await api.get('/admin/workflow-configs');
    return (res.data?.data || res.data) as any[];
  },

  getConfig: async (id: number) => {
    const res = await api.get(`/admin/workflow-configs/${id}`);
    return (res.data?.data || res.data) as any;
  },

  createConfig: async (data: any) => {
    const res = await api.post('/admin/workflow-configs', data);
    return (res.data?.data || res.data) as any;
  },

  updateConfig: async (id: number, data: any) => {
    const res = await api.put(`/admin/workflow-configs/${id}`, data);
    return (res.data?.data || res.data) as any;
  },

  deleteConfig: async (id: number) => {
    await api.delete(`/admin/workflow-configs/${id}`);
  },

  toggleActive: async (id: number) => {
    const res = await api.post(`/admin/workflow-configs/${id}/toggle-active`);
    return (res.data?.data || res.data) as any;
  },

  addStep: async (configId: number, data: any) => {
    const res = await api.post(`/admin/workflow-configs/${configId}/steps`, data);
    return (res.data?.data || res.data) as any;
  },

  updateStep: async (configId: number, stepId: number, data: any) => {
    const res = await api.put(`/admin/workflow-configs/${configId}/steps/${stepId}`, data);
    return (res.data?.data || res.data) as any;
  },

  deleteStep: async (configId: number, stepId: number) => {
    await api.delete(`/admin/workflow-configs/${configId}/steps/${stepId}`);
  },

  reorderSteps: async (configId: number, steps: { id: number; step_order: number }[]) => {
    const res = await api.post(`/admin/workflow-configs/${configId}/steps/reorder`, { steps });
    return (res.data?.data || res.data) as any;
  },

  getStrategies: async () => {
    const res = await api.get('/admin/workflow-configs/strategies');
    return (res.data?.data || res.data) as {
      strategies: Array<{ value: string; label: string; description: string }>;
      resolution_levels: Array<{ value: string; label: string }>;
    };
  },
};
